import type {
  BrandLearningAggregates,
  BrandLearningPreviewResponse,
  BrandLearningSource,
  BrandLearningSummary,
  RawPostAnalysis,
} from '@mondaygpt/types'
import { BrandLearningService } from '../../../openapi/services/BrandLearningService'
import type { SetBrandLearningOverrideDto } from '../../../openapi/models/SetBrandLearningOverrideDto'
import type { PreviewBrandLearningDto } from '../../../openapi/models/PreviewBrandLearningDto'
import type { SeedBrandLearningDto } from '../../../openapi/models/SeedBrandLearningDto'

/**
 * Thin re-exports over the generated `BrandLearningService` (auto-emitted by
 * `yarn openapi` from `BrandLearningController`). Kept as named wrappers so
 * call-sites read close to the spec (`fetchBrandLearning(businessId)` beats
 * `BrandLearningService.brandLearningControllerGetSummaryV1(businessId)`),
 * but the actual request shapes / paths / methods come from the generator —
 * a controller rename will surface as a TS error here, not as a silent
 * runtime 404.
 *
 * Spec: `docs/superpowers/specs/2026-05-20-brand-learning-from-instagram-design.md`.
 *
 * The cast on the `GET` return type is load-bearing: the generated DTO for
 * `aggregates` is `Record<string, any> | null` because the swagger schema is
 * intentionally loose for the jsonb column. The shared `BrandLearningSummary`
 * carries the precise type the server actually returns. Follow-up: tighten
 * the `@ApiProperty` decorators on `BrandLearningSourceSummaryDto` to emit
 * proper string/nullable types, then drop the cast.
 */

/**
 * `GET /v1/businesses/:businessId/brand-learning`
 *
 * Returns per-source aggregates + exemplar URLs + overrides + history. The
 * server returns `{ sources: [] }` (not 404) when no learning has been
 * persisted yet — callers should still treat empty arrays as "show skeletons
 * / no data yet".
 */
export function fetchBrandLearning(businessId: string): Promise<BrandLearningSummary> {
  return BrandLearningService.brandLearningControllerGetSummaryV1(
    businessId
  ) as unknown as Promise<BrandLearningSummary>
}

/**
 * `PUT /v1/businesses/:businessId/brand-learning/:source/overrides`
 *
 * Persists a user override at a dotted field path (e.g.
 * `visual.peoplePresence`). Overrides win on read inside `BrandResolver`.
 */
export function setBrandLearningOverride(
  businessId: string,
  source: BrandLearningSource,
  fieldPath: string,
  value: unknown
): Promise<void> {
  // The generated DTO types `value` as `Record<string, any>` because the
  // swagger schema can't express `unknown`. Cast through the DTO so this
  // wrapper still tracks the generated signature.
  const body = { fieldPath, value } as unknown as SetBrandLearningOverrideDto
  return BrandLearningService.brandLearningControllerSetOverrideV1(
    businessId,
    source,
    body
  ) as unknown as Promise<void>
}

/**
 * `DELETE /v1/businesses/:businessId/brand-learning/:source/overrides`
 *
 * Removes a single override, reverting the field to the mined value.
 */
export function clearBrandLearningOverride(
  businessId: string,
  source: BrandLearningSource,
  fieldPath: string
): Promise<void> {
  return BrandLearningService.brandLearningControllerClearOverrideV1(businessId, source, {
    fieldPath,
  }) as unknown as Promise<void>
}

/**
 * `POST /v1/businesses/:businessId/brand-learning/:source/refresh`
 *
 * Fire-and-forget. The server returns 202 immediately; consumers listen on
 * `PusherEvent.BrandLearningCompleted` (via `useBrandLearningPusher`) to know
 * when mining finishes and re-fetch the summary.
 */
export function refreshBrandLearning(
  businessId: string,
  source: BrandLearningSource
): Promise<void> {
  return BrandLearningService.brandLearningControllerRefreshV1(
    businessId,
    source
  ) as unknown as Promise<void>
}

/**
 * `POST /v1/businesses/:businessId/brand-learning/:source/reset`
 *
 * Drops the persisted learning row for this source — privacy controls + the
 * Instagram-disconnect path. Custom overrides are lost.
 */
export function resetBrandLearning(businessId: string, source: BrandLearningSource): Promise<void> {
  return BrandLearningService.brandLearningControllerResetV1(
    businessId,
    source
  ) as unknown as Promise<void>
}

/**
 * `POST /v1/brand-learning/preview`
 *
 * Onboarding-only: mines the supplied IG handle / website URL and returns a
 * transient `BrandLearningPreviewResponse` (summary + rawPerPostData). No DB
 * write. The client should round-trip `rawPerPostData` back to
 * `seedBrandLearningFromPreview` after `createBusinessAction`.
 *
 * Rate-limited 3/60s per JWT user. Throws 503 if the kill switch is on.
 */
export function previewBrandLearning(input: {
  source: 'instagram' | 'website'
  instagramHandle?: string
  websiteUrl?: string
}): Promise<BrandLearningPreviewResponse> {
  return BrandLearningService.brandLearningPreviewControllerPreviewV1(
    input as unknown as PreviewBrandLearningDto
  ) as unknown as Promise<BrandLearningPreviewResponse>
}

/**
 * `POST /v1/businesses/:businessId/brand-learning/:source/seed`
 *
 * Persists a cached preview blob (from `previewBrandLearning`) as the first
 * BrandLearning row for this business. Idempotent — no-op if a row already
 * exists with non-null `lastRefreshedAt`. Server validates that the previewed
 * handle/URL matches the current brand kit.
 *
 * Called from `DarkOnboardingFlow.persistBrandKit` after `patchBrandKit`
 * succeeds, so the brand kit has the handle/URL set before the validation runs.
 */
export function seedBrandLearningFromPreview(input: {
  businessId: string
  source: BrandLearningSource
  aggregates: BrandLearningAggregates | null
  rawPerPostData: RawPostAnalysis[]
  previewedWith: { instagramHandle?: string; websiteUrl?: string }
}): Promise<void> {
  const body = {
    aggregates: input.aggregates,
    rawPerPostData: input.rawPerPostData,
    previewedWith: input.previewedWith,
  } as unknown as SeedBrandLearningDto
  return BrandLearningService.brandLearningControllerSeedFromPreviewV1(
    input.businessId,
    input.source,
    body
  ) as unknown as Promise<void>
}
